/**
 * Converts HTML to Markdown.
 * Handles: bold, italic, underline, links, images, lists, line breaks, paragraphs.
 */

interface ConversionRule {
  pattern: RegExp;
  replacement: string | ((match: string, ...groups: string[]) => string);
}

const CONVERSION_RULES: ConversionRule[] = [
  // Images (must be before links to avoid conflicts)
  { pattern: /<img[^>]+src="([^"]*)"[^>]*alt="([^"]*)"[^>]*\/?>/gi, replacement: '![$2]($1)' },
  { pattern: /<img[^>]+alt="([^"]*)"[^>]*src="([^"]*)"[^>]*\/?>/gi, replacement: '![$1]($2)' },
  { pattern: /<img[^>]+src="([^"]*)"[^>]*\/?>/gi, replacement: '![image]($1)' },
  // Bold
  { pattern: /<(strong|b)>(.*?)<\/\1>/gi, replacement: '**$2**' },
  // Italic
  { pattern: /<(em|i)>(.*?)<\/\1>/gi, replacement: '*$2*' },
  // Underline — strip tags, no standard markdown equivalent
  { pattern: /<\/?u>/gi, replacement: '' },
  // Links
  { pattern: /<a[^>]+href="([^"]*)"[^>]*>(.*?)<\/a>/gi, replacement: '[$2]($1)' },
  // Line breaks
  { pattern: /<br\s*\/?>/gi, replacement: '\n' },
  // Paragraphs and divs — convert to double newlines to preserve block spacing
  { pattern: /<\/p>\s*<p[^>]*>/gi, replacement: '\n\n' },
  { pattern: /<\/div>\s*<div[^>]*>/gi, replacement: '\n\n' },
  { pattern: /<\/?p[^>]*>/gi, replacement: '\n' },
  { pattern: /<\/?div[^>]*>/gi, replacement: '\n' },
  // Headings
  { pattern: /<h1[^>]*>(.*?)<\/h1>/gi, replacement: '\n\n# $1\n\n' },
  { pattern: /<h2[^>]*>(.*?)<\/h2>/gi, replacement: '\n\n## $1\n\n' },
  { pattern: /<h3[^>]*>(.*?)<\/h3>/gi, replacement: '\n\n### $1\n\n' },
];

const decodeHtmlEntities = (text: string): string => {
  const entities: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&nbsp;': ' ',
  };
  return Object.entries(entities).reduce(
    (result, [entity, char]) => result.replace(new RegExp(entity, 'g'), char),
    text,
  );
};

const removeRemainingTags = (text: string): string =>
  text.replace(/<[^>]+>/g, '');

const normalizeWhitespace = (text: string): string =>
  text
    .replace(/\n{3,}/g, '\n\n') // Max 2 consecutive newlines
    .replace(/[ \t]+/g, ' ') // Collapse spaces
    .trim();

// Strip wrapper tags (<p>, <div>) from inside list item content so they don't
// introduce newlines that break the "1. text" / "- text" format.
const stripBlockWrappers = (content: string): string =>
  content.replace(/<\/?(p|div)[^>]*>/gi, '').trim();

// Convert lists before inline rules so we can distinguish <ol> from <ul>
const convertLists = (html: string): string => {
  // Convert ordered list items with numbering
  let result = html.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (_match, inner: string) => {
    let index = 1;
    return inner.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (_liMatch: string, content: string) => {
      return `\n${index++}. ${stripBlockWrappers(content)}`;
    });
  });

  // Convert unordered list items with dashes
  result = result.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (_match, inner: string) => {
    return inner.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (_liMatch: string, content: string) => {
      return `\n- ${stripBlockWrappers(content)}`;
    });
  });

  return result;
};

export const htmlToMarkdown = (html: string): string => {
  let result = html;

  // Convert lists first (need list wrapper context to distinguish ol vs ul)
  result = convertLists(result);

  // Apply inline conversion rules
  for (const rule of CONVERSION_RULES) {
    result = result.replace(rule.pattern, rule.replacement as string);
  }

  result = removeRemainingTags(result);
  result = decodeHtmlEntities(result);
  result = normalizeWhitespace(result);

  return result;
};
